import { DollarOutlined, UserOutlined } from '@ant-design/icons'
import { Spin } from 'antd'
import { StakingNFTOverview } from 'Modules'
import { useCallback, useEffect, useState } from 'react'

import { AppAdmin } from 'api/app-admin'
import { StakingNFTPoolType } from 'constants/staking-nft'
import { logInfo } from 'utils/logger'

type OverviewData = Record<StakingNFTPoolType, StakingNFTOverview | null> & {
  totalNftStaked: number
  totalUsers: number
}

const Overview = ({ id }: { id?: string }) => {
  const [loadingData, setLoadingData] = useState(false)
  const [data, setData] = useState<OverviewData>({
    MEY: null,
    USDC: null,
    totalNftStaked: 0,
    totalUsers: 0,
  })

  const getData = useCallback(async () => {
    setLoadingData(true)
    try {
      const res = await AppAdmin.stakingTokenizeOverviewAnalyze(id)
      const data = res.data.data.pools || [res.data.data] || []

      const overviewData: OverviewData = {
        MEY: null,
        USDC: null,
        totalNftStaked: 0,
        totalUsers: 0,
      }

      data.forEach((pool: StakingNFTOverview) => {
        overviewData.totalUsers += pool.totalUsers
        overviewData.totalNftStaked += pool.totalNftStaked

        if (pool.poolName === StakingNFTPoolType.MEY) {
          overviewData.MEY = pool
        } else if (pool.poolName === StakingNFTPoolType.USDC) {
          overviewData.USDC = pool
        }
      })

      setData(overviewData)
    } catch (err) {
      logInfo(err)
    } finally {
      setLoadingData(false)
    }
  }, [id])

  useEffect(() => {
    getData()
  }, [getData, id])

  const poolType = data.MEY && data.USDC ? 'USDC - MEY' : data.USDC ? 'USDC' : 'MEY'

  return (
    <div className="wrap-dashboard">
      <div className="wrap-box">
        <div className="title">Staked Overview</div>
        <div className="wrap-columns">
          <div className="columns">
            <div className="colum w-4 w-sm-6">
              <div className="guide-box">
                <div className="box-content">
                  <div className="left-s">
                    <div className="txt">Total Users</div>
                    <div className="number">
                      {loadingData ? (
                        <Spin />
                      ) : (
                        new Intl.NumberFormat('en-US').format(data.totalUsers || 0)
                      )}
                    </div>
                  </div>
                  <div className="right-s">
                    <div className="icon">
                      <UserOutlined />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="colum w-4">
              <div className="guide-box">
                <div className="box-content">
                  <div className="left-s">
                    <div className="txt">Total NFT Staked</div>
                    <div className="number">
                      {loadingData ? (
                        <Spin />
                      ) : (
                        new Intl.NumberFormat('en-US').format(data.totalNftStaked || 0)
                      )}
                    </div>
                  </div>
                  <div className="right-s">
                    <div className="icon">
                      <DollarOutlined />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="colum w-4">
              <div className="guide-box">
                <div className="box-content">
                  <div className="left-s">
                    <div className="txt">Total Reward Claimed ({poolType})</div>
                    <div className="number">
                      {loadingData ? (
                        <Spin />
                      ) : (
                        <div>
                          {data.USDC?.totalRewardClaimed !== undefined && (
                            <span>
                              {new Intl.NumberFormat('en-US').format(
                                data.USDC?.totalRewardClaimed || 0
                              )}
                            </span>
                          )}

                          {poolType !== 'USDC' && poolType !== 'MEY' && ' - '}

                          {data.MEY?.totalRewardClaimed != undefined && (
                            <span>
                              {new Intl.NumberFormat('en-US').format(
                                data.MEY?.totalRewardClaimed || 0
                              )}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="right-s">
                    <div className="icon">
                      <DollarOutlined />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="colum w-4">
              <div className="guide-box">
                <div className="box-content">
                  <div className="left-s">
                    <div className="txt">Estimate Reward Available Claim ({poolType})</div>
                    <div className="number">
                      {loadingData ? (
                        <Spin />
                      ) : (
                        <div>
                          {data.USDC?.estimateRewardAvailableClaim !== undefined && (
                            <span>
                              {new Intl.NumberFormat('en-US').format(
                                data.USDC?.estimateRewardAvailableClaim || 0
                              )}
                            </span>
                          )}

                          {poolType !== 'USDC' && poolType !== 'MEY' && ' - '}

                          {data.MEY?.estimateRewardAvailableClaim != undefined && (
                            <span>
                              {new Intl.NumberFormat('en-US').format(
                                data.MEY?.estimateRewardAvailableClaim || 0
                              )}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="right-s">
                    <div className="icon">
                      <DollarOutlined />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="colum w-4">
              <div className="guide-box">
                <div className="box-content">
                  <div className="left-s">
                    <div className="txt">Estimate Reward Distributed ({poolType})</div>
                    <div className="number">
                      {loadingData ? (
                        <Spin />
                      ) : (
                        <div>
                          {data.USDC?.estimateRewardDistributed !== undefined && (
                            <span>
                              {new Intl.NumberFormat('en-US').format(
                                data.USDC?.estimateRewardDistributed || 0
                              )}
                            </span>
                          )}

                          {poolType !== 'USDC' && poolType !== 'MEY' && ' - '}

                          {data.MEY?.estimateRewardDistributed != undefined && (
                            <span>
                              {new Intl.NumberFormat('en-US').format(
                                data.MEY?.estimateRewardDistributed || 0
                              )}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="right-s">
                    <div className="icon">
                      <DollarOutlined />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Overview
